import { screenWidthSelector } from 'admin/state/modules/common/selectors';
import ContentDurationField from 'common/components/modules/content-duration';
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux';
import PrivateMessageToMember from '../private-message-to-member';
import SwitchMembershipTagMembers from '../tag-members';
import moment from 'moment';
import { getCountState } from 'common/utils/utils';
import SwitchMembershipNotes from '../notes/indexV2';
import Icon from 'common/components/elements/icons';


const DURATION_DATA = [
   {
      name: '1 hour',
      value: 60,
      isDefault: true,
   },
   {
      name: '2 hours',
      value: 120,
      isDefault: true,
   },
   {
      name: '6 hours',
      value: 360,
      isDefault: true,
   },
   {
      name: '12 hours',
      value: 720,
      isDefault: true,
   },
   {
      name: '24 hours',
      value: 1440,
      isDefault: true,
   },
   {
      name: '2 days',
      value: 2880,
      isDefault: true,
   },
   {
      name: '7 days',
      value: 10080,
      isDefault: true,
   },
   {
      name: '14 days',
      value: 20160,
      isDefault: true,
   },
   {
      name: '30 days',
      value: 43200,
      isDefault: true,
   },
]

const SwitchMembershipDuration = ({
   duration,
   updatePayload,
   authUser,
   isMobile,
   errorsData,
   setErrorState,
   modalContentWrapperId,
   disableDuration = false,
}) => {
   const screenWidth = useSelector(screenWidthSelector)

   const handleDurationFieldChange = (data = {}) => {
      if('enabled' in data && data.enabled) {
         setTimeout(() => {
            const modalWrapper = document.getElementById(modalContentWrapperId)
            if(modalWrapper) {
               modalWrapper.scrollTo({
                  top: modalWrapper.scrollHeight,
                  behavior: 'smooth', // optional for smooth scrolling
               });
            }
         }, 10)
      }
      updatePayload({
         duration: {
            ...duration,
            ...data,
         },
      })
   }

   const datePickerCustomHandler = (value) => {
      if(!value) return;

      const selected = moment(value)
      const targetMidnight = selected.clone().startOf('day');
      const hoursDiff = targetMidnight.diff(moment(), 'minutes');
      handleDurationFieldChange({
         value_in_minutes: hoursDiff,
      })
   }

   const getCustomDurationDisplayNameByValue = (durationValue) => {
      const days = Math.floor(durationValue / (60 * 24));
      const hours = Math.floor((durationValue % (60 * 24)) / 60);
      const minutes = Math.floor(durationValue % 60);

      const parts = [];

      if(days > 0) {
         parts.push(getCountState(days, 'day'));
      }
      if(hours > 0) {
         parts.push(getCountState(hours, 'hour'));
      }
      if(minutes > 0 || parts.length === 0) {
         parts.push(getCountState(minutes, 'minute'));
      }

      return parts.join(', ');
   }

   return (
      <ContentDurationField
         contentType='switch_membership'
         description='Set the amount of time after which the membership will automatically switch back to the original'
         durationListData={ DURATION_DATA }
         onChange={ (data) => {
            let updateData = {}
            if('duration_enabled' in data) {
               updateData.enabled = data.duration_enabled
            }
            if('duration' in data) {
               updateData.value_in_minutes = data.duration
            }
            if('show_public_countdown' in data) {
               updateData.show_countdown_to_member = data.show_public_countdown
            }

            handleDurationFieldChange(updateData)
         } }
         isDurationEnabled={ duration.enabled }
         useDefaultStyles={ false }
         className='p-4 rounded-lg !bg-grey-main'
         duration={ duration.value_in_minutes }
         durationKeyName='duration'
         showPublicCountDown={ duration.show_countdown_to_member }
         textClassName='!text-base !font-medium !leading-5'
         screenWidth={ screenWidth }
         datePickerCustomHandler={ datePickerCustomHandler }
         getCustomDurationDisplayNameByValue={ getCustomDurationDisplayNameByValue }
         customDatePickerMinDate={ moment().add(1, 'days').startOf('day').toDate() }
         countDownCheckboxLabelComponent={ (
            <div
               className='flex items-center gap-2'
            >
               <span
                  className='text-sm font-normal leading-[18px] text-major'
               >
                  Show countdown to member
               </span>
               <Icon
                  name='help'
                  color='placeholder'
                  data-tooltip-id='ams-top-tooltip'
                  data-tooltip-content='Display a timer to show members when their switch expires'
               />
            </div>
         ) }
         durationDropdownScrollIntoViewOnOpenContent
         disableSwitch={ disableDuration }
         tooltipContent='Duration is required for guests/expired members'
      >
         <PrivateMessageToMember
            isMobile={ isMobile }
            showOnlyView
            areaName='message_to_member_when_duration_ends'
            value={ duration.message_to_member_when_duration_ends }
            onChange={ (name, value) => {
               handleDurationFieldChange({
                  [name]: value,
               })
            } }
            title='Message to the member when the duration ends'
            errorMessage={ errorsData?.['duration.message_to_member_when_duration_ends']?.[0] }
            clearErrorState={ () => setErrorState({ 'duration.message_to_member_when_duration_ends': null }) }
         />
         <SwitchMembershipNotes
            isMobile={ isMobile }
            title='Private note when the duration ends'
            onChange={ (value) => {
               handleDurationFieldChange({
                  private_note_when_duration_ends: value,
               })
            } }
            showOnlyView
            initialValue={ duration.private_note_when_duration_ends }
         />
         <SwitchMembershipTagMembers
            selectedMemberTagIds={ duration.tags_to_remove_when_duration_ends }
            onSelectTags={ (selectedMemberTagIds = []) => {
               handleDurationFieldChange({
                  tags_to_remove_when_duration_ends: selectedMemberTagIds,
               })
            } }
            isOnDurationField
            title='Remove tag from members'
            description='Remove these tags when the duration ends'
         />

      </ContentDurationField>
   )
}

SwitchMembershipDuration.propTypes = {
   duration: PropTypes.any,
   updatePayload: PropTypes.func,
   authUser: PropTypes.object,
   isMobile: PropTypes.bool,
   errorsData: PropTypes.object,
   setErrorState: PropTypes.func,
   modalContentWrapperId: PropTypes.string,
   disableDuration: PropTypes.bool,
}

export default SwitchMembershipDuration;
